<template>
  <div class="maintainance-task-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('maintainanceTaskManage.page.title')" :subtitle="$t('maintainanceTaskManage.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.planUserName" clearable
            :placeholder="$t('maintainanceTaskManage.planUserNamePlaceholder')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-date-picker v-model="searchForm.startTime" type="datetime" class="w-full"
            value-format="yyyy-MM-dd HH:mm:ss" :placeholder="$t('maintainanceTaskManage.startTimePlaceholder')" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-date-picker v-model="searchForm.endTime" type="datetime" class="w-full"
            value-format="yyyy-MM-dd HH:mm:ss" :placeholder="$t('maintainanceTaskManage.endTimePlaceholder')" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.planName" clearable
            :placeholder="$t('maintainanceTaskManage.planNamePlaceholder')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="searchForm.state" clearable class="w-full"
            :placeholder="$t('maintainanceTaskManage.statePlaceholder')">
            <el-option v-for="item in stateTypes" :key="item.statusCd" :label="item.name" :value="item.statusCd" />
          </el-select>
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('maintainanceTaskManage.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('maintainanceTaskManage.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('maintainanceTaskManage.listTitle') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('maintainanceTaskManage.page.empty')" @retry="getList">
        <el-table-column prop="taskId" :label="$t('maintainanceTaskManage.taskId')" align="center" width="150">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.taskId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="planName" :label="$t('maintainanceTaskManage.planName')" align="left" min-width="150"
          show-overflow-tooltip />
        <!-- 计划保养时间：起止两行 -->
        <el-table-column :label="$t('maintainanceTaskManage.timeRange')" align="center" width="160">
          <template slot-scope="scope">
            <div class="ds-num">{{ scope.row.planInsTime || '-' }}</div>
            <div class="ds-num ds-cell-sub">{{ scope.row.planEndTime || '-' }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="actInsTime" :label="$t('maintainanceTaskManage.actInsTime')" align="center" width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.actInsTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="originalPlanUserName" :label="$t('maintainanceTaskManage.originalPlanUser')"
          align="center" width="110">
          <template slot-scope="scope">
            {{ scope.row.originalPlanUserName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="planUserName" :label="$t('maintainanceTaskManage.currentPlanUser')" align="center"
          width="100" />
        <el-table-column prop="transferDesc" :label="$t('maintainanceTaskManage.transferDesc')" align="left"
          min-width="130" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.transferDesc || '-' }}
          </template>
        </el-table-column>
        <!-- 状态 Tag：20200408（已超时）/ 20200409（缺勤）用危险色 -->
        <el-table-column prop="stateName" :label="$t('maintainanceTaskManage.state')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag :type="resolveState(scope.row)" :label="scope.row.stateName || '-'" />
          </template>
        </el-table-column>
        <el-table-column :label="$t('maintainanceTaskManage.operation')" align="center" width="190" fixed="right">
          <template slot-scope="scope">
            <el-button v-if="scope.row.state === '20200406' || scope.row.state === '20200405'" size="mini" type="text"
              @click="handleTransfer(scope.row)">
              {{ $t('maintainanceTaskManage.transfer') }}
            </el-button>
            <el-button size="mini" type="text" @click="handleDetail(scope.row)">
              {{ $t('maintainanceTaskManage.detail') }}
            </el-button>
            <el-button size="mini" type="text" class="ds-btn-danger" v-if="hasPrivilege('502022031612550001')"
              @click="handleDelete(scope.row)">
              {{ $t('maintainanceTaskManage.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('maintainanceTaskManage.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <!-- 子组件 -->
    <maintainance-task-transfer ref="transferModal" @success="handleSuccess" />
    <maintainance-task-detail ref="detailModal" />
    <delete-maintainance-task ref="deleteModal" @success="handleSuccess" />
  </div>
</template>

<script>
import { listMaintainanceTask } from '@/api/inspection/maintainanceTaskManageApi'
import { getDict } from '@/api/community/communityApi'
import { getCommunityId } from '@/api/community/communityApi'
import MaintainanceTaskTransfer from '@/components/inspection/maintainanceTaskTransfer'
import MaintainanceTaskDetail from '@/components/inspection/maintainanceTaskDetail'
import DeleteMaintainanceTask from '@/components/inspection/deleteMaintainanceTask'
import { resolveStatus } from '@/utils/statusMap'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'MaintainanceTaskManageList',
  components: {
    MaintainanceTaskTransfer,
    MaintainanceTaskDetail,
    DeleteMaintainanceTask,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        planUserName: '',
        startTime: '',
        endTime: '',
        planName: '',
        state: ''
      },
      stateTypes: [],
      tableData: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      communityId: ''
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.getDictData()
    this.getList()
  },
  methods: {
    async getDictData() {
      try {
        const data = await getDict('maintainance_task', 'state')
        this.stateTypes = Array.isArray(data) ? data : []
      } catch (error) {
        console.error('获取字典数据失败:', error)
      }
    },
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          page: this.pagination.current,
          row: this.pagination.size,
          communityId: this.communityId,
          ...this.searchForm
        }
        const res = await listMaintainanceTask(params)

        // 兼容后端在「无匹配数据」时返回 { code:0, records:0 } 而不带 data 字段的情况。
        this.tableData = Array.isArray(res.data) ? res.data : []
        this.pagination.total = res.total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('maintainanceTaskManage.page.loadFailed')
        this.$message.error(this.$t('maintainanceTaskManage.fetchError'))
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm = {
        planUserName: '',
        startTime: '',
        endTime: '',
        planName: '',
        state: ''
      }
      this.pagination.current = 1
      this.getList()
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this.getList()
    },
    handleTransfer(row) {
      this.$refs.transferModal.open(row)
    },
    handleDetail(row) {
      this.$refs.detailModal.open(row)
    },
    handleDelete(row) {
      this.$refs.deleteModal.open(row)
    },
    handleSuccess() {
      this.getList()
    },
    /**
     * 状态语义类型：20200408「已超时」/ 20200409「缺勤」为危险色，其余按文案语义推断。
     */
    resolveState(row) {
      if (!row) return 'muted'
      return resolveStatus(row.state, {
        domain: 'maintenanceTask',
        text: row.stateName || ''
      }).type
    }
  }
}
</script>

<style lang="scss" scoped>
.maintainance-task-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 表格次要文字 */
.ds-cell-sub {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
